import React, { useState } from "react";
import {
  AppBar,
  Avatar,
  Button,
  Toolbar,
  Typography,
  Stack,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import LogOutIcon from "@mui/icons-material/Logout";
import { useThemeMode } from "../common/theme/theme-context";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import localStorageHelper from "../../app/localStorage";
import packageJson from "../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import { useAuth } from "../../app/hooks/useAuth";
import { clearCustomerLocationLookupCaches } from "../common/Utils/cacheUtil";
import SemanticSearchBox from "../semanticSearch/components/AiSearchBox";
import WorkOrderDialogContainer from "../workOrder/WorkOrderDialogContainerMain";

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Nav = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [resourceOpen, setResourceOpen] = useState<null | HTMLElement>(null);
  const [userName] = useState<string | "">(localStorage.getItem("username"));
  const open = Boolean(resourceOpen);
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<number | null>(
    null,
  );

  const handleLogOutClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const cacheUser =
      localStorage.getItem("username") || "currentUserIdentifier";
    clearCustomerLocationLookupCaches(cacheUser);
    setResourceOpen(event.currentTarget);
    if (user && user.userNameAndCompany) {
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "handleLogOut",
        {
          elapsed_ms: "124",
          title: "Navigation Bar",
          componentName: "navbar.tsx",
          version: packageJson.version,
        },
      );
    }

    // Remove all user-related keys
    localStorage.removeItem("authToken");
    localStorage.removeItem("username");
    localStorage.removeItem("user");
    localStorage.removeItem("queueAPI");
    localStorage.removeItem("WorkOrderStatuses");
    localStorage.removeItem("workOrderDashboard");
    // Optionally, remove any other user-specific keys here

    // Optionally, clear sessionStorage as well
    sessionStorage.clear();

    logout();
    // Dispatch custom event for theme context to update
    window.dispatchEvent(new CustomEvent("userChanged"));
    navigate("/", { replace: true });
  };
  const { toggleTheme, mode } = useThemeMode();

  const handleWorkOrderSelect = (workOrderId: number) => {
    setSelectedWorkOrderId(workOrderId);
    setShowWorkOrderDialog(true);
  };

  const handleCloseDialog = () => {
    setShowWorkOrderDialog(false);
    setSelectedWorkOrderId(null);
  };

  // Left navigation moved into Sidebar
  return (
    <>
      <AppBar
        sx={{
          bgcolor: mode === "light" ? "white" : "#2d2d2d",
          borderBottom: mode === "light" ? "1px solid lightgray" : "",
          animation: `${slideDown} 0.5s ease-out`,
        }}
        position="static"
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 0 }}>
            {mode === "light" ? (
              <Box
                onClick={() => navigate("/QueueBoard")}
                sx={{
                  width: "148px",
                  height: "45px",
                  cursor: "pointer",
                  animation: `${fadeInScale} 0.6s ease-out 0.2s both`,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
                component="img"
                src={"../aspen-logo-service-queue.svg"}
              />
            ) : (
              <Box
                onClick={() => navigate("/QueueBoard")}
                sx={{
                  width: "148px",
                  height: "45px",
                  cursor: "pointer",
                  animation: `${fadeInScale} 0.6s ease-out 0.2s both`,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
                component="img"
                src={"../aspen-logo-service-queue-white-2x.svg"}
              />
            )}
          </Typography>
          {/* <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <SemanticSearchBox onWorkOrderSelect={handleWorkOrderSelect} />
          </Box> */}
          <Box sx={{ flexGrow: 1 }} />
          <Stack
            direction={"row"}
            spacing={1}
            sx={{
              marginRight: "35px",
              animation: `${fadeInScale} 0.5s ease-out 0.3s both`,
            }}
          >
            <Tooltip title={userName}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: 36,
                  maxWidth: 40,
                }}
              >
                <Avatar
                  sx={{ bgcolor: "secondary.light", width: 32, height: 32 }}
                >
                  {(() => {
                    if (!userName) return "";
                    const words = userName.trim().split(" ");
                    return words.length === 1
                      ? words[0].charAt(0).toUpperCase()
                      : (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
                  })()}
                </Avatar>
              </Box>
            </Tooltip>
            <Tooltip
              title={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: 36,
                  maxWidth: 40,
                }}
              >
                <IconButton onClick={toggleTheme} color="inherit">
                  {mode === "light" ? (
                    <DarkModeIcon sx={{ color: "#2371E7" }} />
                  ) : (
                    <LightModeIcon />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: 36,
                maxWidth: 40,
              }}
            >
              <Button
                color="inherit"
                id="resources-button"
                onClick={handleLogOutClick}
                aria-controls={open ? "resources-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                  minWidth: 0,
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <LogOutIcon sx={{ color: mode === "light" ? "#2371E7" : "" }} />
              </Button>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>
      {showWorkOrderDialog && selectedWorkOrderId && (
        <WorkOrderDialogContainer
          showHideWoPopup={handleCloseDialog}
          InvoiceDocIdentity={selectedWorkOrderId}
          open={showWorkOrderDialog}
        />
      )}
    </>
  );
};

export default Nav;
